// Bounded retry for idempotent API requests.
//
// Born from the POS order-create stalls incident: Heroku kills responses at
// 30s while the server-side create still commits, so a timeout-less axios
// request left the POS spinner hanging forever. Creates are idempotent
// server-side on localId, so a short client timeout + bounded retry converges
// on the already-created order instead of hanging or duplicating.
//
// ONLY wrap requests that are idempotent server-side.
import axios from 'axios';
import { delay } from '@/utils';

const RETRYABLE_STATUSES = [502, 503, 504];

/**
 * True only for transport-level failures where the request may never have
 * reached (or come back from) the API: no HTTP response at all (network
 * error, or a client timeout — on axios 0.21.x those surface as
 * code 'ECONNABORTED' with no `response`), or a gateway 502/503/504.
 * Every other HTTP status (all 4xx, 500) is a definitive answer and must
 * NOT be retried.
 */
export function isRetryableRequestError(error: unknown): boolean {
  // axios 0.21's isAxiosError reads a property off the payload, so it would
  // throw on null; guard first.
  if (error == null || !axios.isAxiosError(error)) return false;
  if (!error.response) return true;
  return RETRYABLE_STATUSES.includes(error.response.status);
}

export interface IRequestRetryOptions {
  /** Total attempts, including the first one. */
  attempts?: number;
  /** Delays (ms) before each retry; the last value repeats if exhausted. */
  backoffMs?: number[];
}

const DEFAULT_ATTEMPTS = 3;
const DEFAULT_BACKOFF_MS = [1500, 3000];

/**
 * Runs `fn`, retrying only on retryable errors (see isRetryableRequestError)
 * with a fixed backoff between attempts. Rethrows the last error when
 * attempts are exhausted or the error is not retryable.
 */
export async function requestWithRetry<T>(
  fn: () => Promise<T>,
  options: IRequestRetryOptions = {}
): Promise<T> {
  const attempts = options.attempts ?? DEFAULT_ATTEMPTS;
  const backoffMs = options.backoffMs ?? DEFAULT_BACKOFF_MS;

  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt === attempts || !isRetryableRequestError(error)) {
        throw error;
      }
      await delay(backoffMs[Math.min(attempt - 1, backoffMs.length - 1)]);
    }
  }
  // Unreachable (the loop always returns or throws); satisfies TS.
  throw lastError;
}
